import {
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Form, Space, Spin, Tooltip, Typography } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  ACTIONS,
  EDIT_SALES_FUNNEL_PATH,
  FRONT_WARNING,
  LOGS_REGISTRY_PATH,
  SALES_FUNNELS_LEADS_PATH,
  SUCCESS,
  U_IN_PROGRESS,
  U_KEY,
  U_NAME,
  U_SUCCESS,
  U_UNSUCCESSFUL,
} from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { LEADS_SALES_FUNNEL_ID_SEARCH_PARAM } from '@app/pages/Leads/model/salesFunnelSelection';
import { HeaderContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useDeleteSalesFunnelMutation } from '@app/services';
import {
  useLazyCheckForLeadsQuery,
  useLazyListQuery,
} from '@app/services/rtkQuery/SalesFunnels/api';
import { useDeleteSalesFunnelStageMutation } from '@app/services/rtkQuery/SalesFunnelsStages/api';
import type { NFunnels } from '@app/types';
import { shortName } from '@app/utils';

import {
  DELETE_SALES_FUNNEL_CONTENT,
  DELETE_SALES_FUNNEL_HAS_LEADS_CONTENT,
  DELETE_SALES_FUNNEL_STAGE_CONTENT,
  DELETE_SALES_FUNNEL_STAGE_TITLE,
  DELETE_SALES_FUNNEL_TITLE,
} from '@pages/SalesFunnels/SalesFunnelsRegistry/constants/editSalesFunnelModal';

import { Chevron } from '@pages/SalesFunnels/SalesFunnelsRegistry/components';
import {
  CHEVRON_MIN_WIDTH,
  CHEVRON_RECTANGLE_MIN_WIDTH,
  CHEVRONS_GAP,
  ICON_SIZE,
  MAX_NAME_LENGTH,
  RECTANGLE_WIDTH,
} from '@pages/SalesFunnels/SalesFunnelsRegistry/constants';
import type { HeaderFormType } from '@pages/SalesFunnels/types';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

const { Text } = Typography;
const { useForm } = Form;

export const useSalesFunnelsRegistry = () => {
  const { setHeader } = useContext(HeaderContext);

  const { activeBranchId, userId, database } = useUserPrint();
  const { showNotification } = useNotification();

  const [headerForm] = useForm();

  const [deleteSalesFunnelStage] = useDeleteSalesFunnelStageMutation();
  const [deleteSalesFunnel, { isLoading: isStageDeletionLoading }] =
    useDeleteSalesFunnelMutation();

  const [triggerCheckForLeads] = useLazyCheckForLeadsQuery();
  const [getSalesFunnelsList, { isLoading: isListLoading }] =
    useLazyListQuery();

  const [isEditSalesFunnelModalOpen, setIsEditSalesFunnelModalOpen] =
    useState(false);
  const [editableSalesFunnelId, setEditableSalesFunnelId] = useState(0);
  const [currentSalesFunnelStage, setCurrentSalesFunnelStage] =
    useState<NFunnels.SalesFunnelStageFilterListDto>();
  const [isEditModal, setIsEditModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSalesFunnelStage, setIsDeleteSalesFunnelStage] =
    useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [isSalesFunnelHasLeads, setIsSalesFunnelHasLeads] = useState(false);
  const [isCheckingSalesFunnelLeads, setIsCheckingSalesFunnelLeads] =
    useState(false);

  const [allSalesFunnelsList, setAllSalesFunnelsList] = useState<
    NFunnels.SalesFunnelListDto[]
  >([]);
  const [searchedSalesFunnelsList, setSearchedSalesFunnelsList] = useState<
    NFunnels.SalesFunnelListDto[]
  >([]);

  const [inProgressStagesMaxCount, setInProgressStagesMaxCount] = useState(0);
  const [successStagesMaxCount, setSuccessStagesMaxCount] = useState(0);
  const [unsuccessStagesMaxCount, setUnsuccessStagesMaxCount] = useState(0);

  const {
    userAccessRights: {
      SalesFunnels: { IsFunnelEdit, IsFunnelDelete, IsStageCreate },
    },
  } = useNewUserAccessRights();

  const navigate = useNavigate();

  useEffect(() => {
    setHeader({ setIsCreateModalOpen });
  }, [setHeader]);

  const handleOpenSalesFunnelModal = useCallback(
    (salesFunnelId: number, isEditModal: boolean) => {
      setIsEditSalesFunnelModalOpen(true);
      setEditableSalesFunnelId(salesFunnelId);
      setIsEditModal(isEditModal);
    },
    []
  );

  const handleDeleteSalesFunnelStageClick = useCallback(() => {
    setIsCheckingSalesFunnelLeads(false);
    setIsDeleteSalesFunnelStage(true);
    setIsDeleteModalOpen(true);
  }, []);

  const handleDeleteSalesFunnelClick = useCallback(
    async (id: number) => {
      setEditableSalesFunnelId(id);
      setIsSalesFunnelHasLeads(false);
      setIsDeleteSalesFunnelStage(false);
      setIsDeleteModalOpen(true);
      setIsCheckingSalesFunnelLeads(true);

      try {
        const response = await triggerCheckForLeads({
          id,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setIsSalesFunnelHasLeads(response ?? false);
      } catch {
        showNotification(
          'Серверная функция проверки лидов вернула ошибку',
          FRONT_WARNING
        );
      } finally {
        setIsCheckingSalesFunnelLeads(false);
      }
    },
    [triggerCheckForLeads, activeBranchId, showNotification]
  );

  const getSalesFunnelsListHandler = useCallback(async () => {
    if (!activeBranchId) return;

    try {
      const response = await getSalesFunnelsList({
        ActiveBranchId: activeBranchId,
        logs: {
          isLogged: true,
          userId,
          database,
        },
      }).unwrap();

      setSearchedSalesFunnelsList(response);
      setAllSalesFunnelsList(response);
    } catch (error) {
      showNotification(error);
    }
  }, [activeBranchId, database, getSalesFunnelsList, showNotification, userId]);

  const handleDeleteSalesFunnelStage = useCallback(async () => {
    if (!currentSalesFunnelStage)
      return showNotification(
        'Не найдена категория воронки продаж',
        FRONT_WARNING
      );

    try {
      await deleteSalesFunnelStage({
        id: currentSalesFunnelStage?.Id,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      showNotification('Категория воронки продаж успешно удалена', SUCCESS);

      setIsDeleteModalOpen(false);
      setIsEditSalesFunnelModalOpen(false);

      await getSalesFunnelsListHandler();
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    currentSalesFunnelStage,
    deleteSalesFunnelStage,
    getSalesFunnelsListHandler,
    showNotification,
  ]);

  const handleDeleteSalesFunnel = useCallback(async () => {
    if (isCheckingSalesFunnelLeads) return;

    if (!editableSalesFunnelId)
      return showNotification('Не найдена воронка продаж', FRONT_WARNING);

    try {
      await deleteSalesFunnel({
        id: editableSalesFunnelId,
        ActiveBranchId: activeBranchId,
      }).unwrap();

      showNotification('Воронка успешно удалена', SUCCESS);

      await getSalesFunnelsListHandler();
    } catch (error) {
      showNotification(error);
    } finally {
      setIsDeleteModalOpen(false);
    }
  }, [
    activeBranchId,
    deleteSalesFunnel,
    editableSalesFunnelId,
    getSalesFunnelsListHandler,
    isCheckingSalesFunnelLeads,
    showNotification,
  ]);

  const handleResetForm = useCallback(() => {
    headerForm.resetFields();
    setSearchedSalesFunnelsList(allSalesFunnelsList);
  }, [allSalesFunnelsList, headerForm]);

  const handleSearch = useCallback(
    ({ SearchField }: HeaderFormType) => {
      const searchedList = SearchField
        ? allSalesFunnelsList.filter(
            (item) =>
              item.Name?.toLowerCase().includes(SearchField.toLowerCase()) ||
              item.Description?.toLowerCase().includes(
                SearchField.toLowerCase()
              )
          )
        : allSalesFunnelsList;
      setSearchedSalesFunnelsList(searchedList);
    },
    [allSalesFunnelsList]
  );

  const handleDelete = useMemo(
    () =>
      isDeleteSalesFunnelStage
        ? handleDeleteSalesFunnelStage
        : handleDeleteSalesFunnel,
    [
      handleDeleteSalesFunnel,
      handleDeleteSalesFunnelStage,
      isDeleteSalesFunnelStage,
    ]
  );

  const confirmDeletingTitle = useMemo(
    () =>
      isDeleteSalesFunnelStage
        ? DELETE_SALES_FUNNEL_STAGE_TITLE
        : DELETE_SALES_FUNNEL_TITLE,
    [isDeleteSalesFunnelStage]
  );

  const confirmDeletingContent = useMemo(() => {
    const checkingLeadsText =
      !isDeleteSalesFunnelStage && isCheckingSalesFunnelLeads ? (
        <div style={{ marginBottom: '12px' }}>
          <Spin size="small" /> Проверяем наличие лидов в воронке...
        </div>
      ) : null;

    const hasLeadsText = isSalesFunnelHasLeads ? (
      <Alert
        description={DELETE_SALES_FUNNEL_HAS_LEADS_CONTENT}
        type="warning"
        style={{ marginBottom: '12px' }}
        showIcon
      />
    ) : null;

    return (
      <>
        {checkingLeadsText}
        {hasLeadsText}
        {isDeleteSalesFunnelStage
          ? DELETE_SALES_FUNNEL_STAGE_CONTENT
          : DELETE_SALES_FUNNEL_CONTENT}
      </>
    );
  }, [
    isCheckingSalesFunnelLeads,
    isDeleteSalesFunnelStage,
    isSalesFunnelHasLeads,
  ]);

  const getChevronRectangleWidth = useCallback(
    (
      successStages: NFunnels.SalesFunnelStageFilterListDto[],
      maxCount: number
    ) => {
      if (maxCount === 0 || successStages.length === 0) {
        return CHEVRON_RECTANGLE_MIN_WIDTH;
      }

      if (successStages.length === maxCount) {
        return CHEVRON_RECTANGLE_MIN_WIDTH;
      }

      const totalWidthForRectangles =
        CHEVRON_MIN_WIDTH * maxCount + CHEVRONS_GAP * (maxCount - 1);

      const occupiedWidth =
        successStages.length * (ICON_SIZE + CHEVRONS_GAP + RECTANGLE_WIDTH) +
        CHEVRONS_GAP * (successStages.length - 1);

      const remainingWidth = totalWidthForRectangles - occupiedWidth;

      return remainingWidth / successStages.length;
    },
    []
  );

  const handleLogsIconClick = useCallback(
    (salesFunnelId: number, event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      event.preventDefault();

      navigate(LOGS_REGISTRY_PATH, {
        state: {
          salesFunnelId,
        },
      });
    },
    [navigate]
  );

  const dataSource = useMemo(
    () =>
      searchedSalesFunnelsList.map(
        ({ Id, Name, Description, Stages, CountUnreadLogs }) => {
          const inProgressStages = Stages.filter((item) => item.Category === 0);
          const successStages = Stages.filter((item) => item.Category === 1);
          const unsuccessStages = Stages.filter((item) => item.Category === 2);

          if (inProgressStages.length > inProgressStagesMaxCount) {
            setInProgressStagesMaxCount(inProgressStages.length);
          }

          if (successStages.length > successStagesMaxCount) {
            setSuccessStagesMaxCount(successStages.length);
          }

          if (unsuccessStages.length > unsuccessStagesMaxCount) {
            setUnsuccessStagesMaxCount(unsuccessStages.length);
          }

          return {
            [U_KEY]: String(Id),
            [U_NAME]: (
              <div className="name-column">
                <span>
                  {Number(CountUnreadLogs) > 0 && (
                    <Tooltip title="У вас есть непрочитанные уведомления. Кликните на иконку, чтобы увидеть их.">
                      <WarningOutlined
                        className="logs-icon"
                        onClick={(event) => handleLogsIconClick(Id, event)}
                      />
                    </Tooltip>
                  )}{' '}
                  {Name && shortName(Name, false, MAX_NAME_LENGTH)}
                </span>
                {Description && (
                  <Text>{shortName(Description, false, MAX_NAME_LENGTH)}</Text>
                )}
              </div>
            ),
            [U_IN_PROGRESS]: (
              <div className="stage-cell">
                {inProgressStages.length
                  ? inProgressStages.map((item) => (
                      <Chevron
                        stage={item}
                        key={item.Id}
                        rectangleWidth={getChevronRectangleWidth(
                          inProgressStages,
                          inProgressStagesMaxCount
                        )}
                        setCurrentSalesFunnelStage={setCurrentSalesFunnelStage}
                        handleOpenSalesFunnelModal={handleOpenSalesFunnelModal}
                        salesFunnelId={Id}
                      />
                    ))
                  : ''}
              </div>
            ),
            [U_SUCCESS]: (
              <div className="stage-cell">
                {successStages.length
                  ? successStages.map((item) => (
                      <Chevron
                        stage={item}
                        key={item.Id}
                        rectangleWidth={getChevronRectangleWidth(
                          successStages,
                          successStagesMaxCount
                        )}
                        setCurrentSalesFunnelStage={setCurrentSalesFunnelStage}
                        handleOpenSalesFunnelModal={handleOpenSalesFunnelModal}
                        salesFunnelId={Id}
                      />
                    ))
                  : ''}
              </div>
            ),
            [U_UNSUCCESSFUL]: (
              <div className="stage-cell">
                {unsuccessStages.length
                  ? unsuccessStages.map((item) => (
                      <Chevron
                        stage={item}
                        key={item.Id}
                        rectangleWidth={getChevronRectangleWidth(
                          unsuccessStages,
                          unsuccessStagesMaxCount
                        )}
                        setCurrentSalesFunnelStage={setCurrentSalesFunnelStage}
                        handleOpenSalesFunnelModal={handleOpenSalesFunnelModal}
                        salesFunnelId={Id}
                      />
                    ))
                  : ''}
              </div>
            ),
            [ACTIONS]: (
              <Space direction="horizontal">
                {IsStageCreate && (
                  <Tooltip title="Добавить этап воронки продаж">
                    <Button
                      icon={
                        <FontAwesomeIcon
                          icon={'fa-regular fa-square-plus' as IconProp}
                          size="lg"
                          className="icon"
                        />
                      }
                      onClick={() => handleOpenSalesFunnelModal(Id, false)}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Просмотреть лидов воронки">
                  <Link
                    to={`${SALES_FUNNELS_LEADS_PATH}?${LEADS_SALES_FUNNEL_ID_SEARCH_PARAM}=${Id}`}
                  >
                    <Button icon={<WechatOutlined />} />
                  </Link>
                </Tooltip>
                {IsFunnelEdit && (
                  <Tooltip title="Редактирование">
                    <Link to={`${EDIT_SALES_FUNNEL_PATH}?id=${Id}`}>
                      <Button icon={<EditOutlined />} />
                    </Link>
                  </Tooltip>
                )}
                {IsFunnelDelete && (
                  <Tooltip title="Удаление">
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteSalesFunnelClick(Id)}
                    />
                  </Tooltip>
                )}
              </Space>
            ),
          };
        }
      ),
    [
      IsFunnelDelete,
      IsFunnelEdit,
      IsStageCreate,
      getChevronRectangleWidth,
      handleDeleteSalesFunnelClick,
      handleLogsIconClick,
      handleOpenSalesFunnelModal,
      inProgressStagesMaxCount,
      searchedSalesFunnelsList,
      successStagesMaxCount,
      unsuccessStagesMaxCount,
    ]
  );

  return {
    isEditSalesFunnelModalOpen,
    setIsEditSalesFunnelModalOpen,
    editableSalesFunnelId,
    currentSalesFunnelStage,
    setCurrentSalesFunnelStage,
    isEditModal,
    headerForm,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDeleteSalesFunnelStageClick,
    getSalesFunnelsListHandler,
    searchedSalesFunnelsList,
    confirmDeletingContent,
    isListLoading,
    handleResetForm,
    handleSearch,
    dataSource,
    handleDelete,
    confirmDeletingTitle,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isStageDeletionLoading:
      isStageDeletionLoading || isCheckingSalesFunnelLeads,
  };
};
